import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './CompanySearch.css';

function CompanySearch({ companies, onCompanySelect, selectedCompany, onAddCompany, onEditCompany, users = [] }) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [searchTerm, setSearchTerm] = useState('');
  const [filterManager, setFilterManager] = useState('');

  // 최초 1회: 담당자 필터 기본값을 '본인'으로 (본인이 담당하는 기업이 있을 때)
  const didInitFilter = useRef(false);
  useEffect(() => {
    if (didInitFilter.current) return;
    if (!currentUserId || companies.length === 0) return; // 데이터 로드 대기
    if (companies.some((c) => c.person_in_charge === currentUserId)) {
      setFilterManager(currentUserId);
    }
    didInitFilter.current = true;
  }, [currentUserId, companies]);

  // 사용자 ID를 이름으로 매핑하는 객체 생성
  const userIdToNameMap = useMemo(() => {
    const map = {};
    users.forEach(user => {
      map[user.id] = user.name || user.id;
    });
    return map;
  }, [users]);

  // 사용자 ID를 담당자 태그 색상으로 매핑하는 객체 생성
  const userIdToColorMap = useMemo(() => {
    const map = {};
    users.forEach(user => {
      if (user.color) map[user.id] = user.color;
    });
    return map;
  }, [users]);

  // 사용자 ID를 이름으로 변환하는 함수
  const getUserName = (userId) => {
    return userIdToNameMap[userId] || userId;
  };

  // 담당자 색상 (미지정 시 기본 파랑)
  const getUserColor = (userId) => {
    return userIdToColorMap[userId] || '#1971C2';
  };

  // 자음 검색을 위한 초성 매핑
  const getChoseong = (str) => {
    const choseong = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
    const hangul = ['가', '까', '나', '다', '따', '라', '마', '바', '빠', '사', '싸', '아', '자', '짜', '차', '카', '타', '파', '하'];
    
    let result = '';
    for (let char of str) {
      const code = char.charCodeAt(0);
      if (code >= 44032 && code <= 55203) { // 한글 범위
        const index = Math.floor((code - 44032) / 588);
        result += choseong[index];
      } else {
        result += char;
      }
    }
    return result;
  };

  // 필터링된 기업 목록
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch = searchTerm === '' || 
        company.name.includes(searchTerm) || 
        company.alt_name.includes(searchTerm) ||
        getChoseong(company.name).includes(searchTerm);
      
      const matchesManager = filterManager === '' || 
        company.person_in_charge === filterManager;
      
      return matchesSearch && matchesManager;
    });
  }, [companies, searchTerm, filterManager]);

  // 담당자 목록 추출 (이름으로 표시)
  const managers = useMemo(() => {
    const uniqueManagerIds = [...new Set(companies.map(company => company.person_in_charge))];
    return uniqueManagerIds.map(userId => ({
      id: userId,
      name: getUserName(userId)
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, userIdToNameMap]);

  return (
    <div className="company-search">
      <div className="search-header">
        <h3>기업 검색</h3>
        <div className="search-stats">
          총 {companies.length}개 기업 중 {filteredCompanies.length}개 표시
        </div>
      </div>

      <div className="search-controls">
        <div className="search-input-group">
          <input
            type="text"
            placeholder="기업명 또는 초성으로 검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearchTerm('')}
              title="검색어 지우기"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <label className="filter-label">담당자</label>
          <select
            value={filterManager}
            onChange={(e) => setFilterManager(e.target.value)}
            className="manager-select"
          >
            <option value="">전체 담당자</option>
            {managers.map(manager => (
              <option key={manager.id} value={manager.id}>{manager.name}</option>
            ))}
          </select>
          <button
            type="button"
            className={`manager-showall-btn${filterManager === '' ? ' active' : ''}`}
            onClick={() => setFilterManager('')}
            title="모든 담당자의 기업 보기"
          >
            모두 보기
          </button>
        </div>
      </div>

      <div className="company-results">
        {filteredCompanies.length > 0 ? (
          <div className="company-list">
            {filteredCompanies.map(company => (
              <div
                key={company.id}
                className={`company-item ${selectedCompany?.id === company.id ? 'selected' : ''}`}
                onClick={() => onCompanySelect(company)}
              >
                <div className="company-content">
                  <div className="company-content-row">
                    <span className="company-manager" style={{ backgroundColor: getUserColor(company.person_in_charge) }}>{getUserName(company.person_in_charge)}</span>
                    <span className="company-name">{company.name}</span>
                  </div>
                  {company.alt_name && company.alt_name !== company.name && (
                    <span className="company-alt-name">{company.alt_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <p>검색 결과가 없습니다.</p>
            <p className="no-results-hint">
              {searchTerm || filterManager ? '검색 조건을 변경해보세요.' : '신규 기업을 등록해보세요.'}
            </p>
          </div>
        )}
      </div>
      
      <div className="add-company-section">
        <button 
          className="add-company-btn"
          onClick={onAddCompany}
        >
          <span className="add-icon">+</span>
          신규 기업 등록
        </button>
      </div>
    </div>
  );
}

export default CompanySearch;
